import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users as UsersIcon, VideoCamera, FileDoc, Sparkle, Clock } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import api, { apiError, coverSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CourseMonogram from "@/components/CourseMonogram";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const COVERS = [
  "https://images.pexels.com/photos/10744384/pexels-photo-10744384.jpeg",
  "https://images.pexels.com/photos/31468533/pexels-photo-31468533.jpeg",
  "https://images.pexels.com/photos/33865563/pexels-photo-33865563.jpeg",
];
const EMPTY = { title: "", description: "", code: "", filiere: "", category: "", cover_image: "", meeting_url: "" };

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [coverFile, setCoverFile] = useState(null);
  const [academic, setAcademic] = useState([]);
  const [pickFiliereId, setPickFiliereId] = useState("");
  const [pickPromotionId, setPickPromotionId] = useState("");

  const load = () => api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) api.get("/academic").then((r) => setAcademic(r.data)).catch(() => {}); }, [open]);

  const canCreate = user.role === "admin" || user.role === "professeur";
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFile, setAiFile] = useState(null);
  const [aiQuiz, setAiQuiz] = useState(true);
  const [aiQuizCount, setAiQuizCount] = useState(8);
  const [aiFiliere, setAiFiliere] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const [aiProgress, setAiProgress] = useState(null);

  const pollJob = (jobId) => new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/generate-course-from-docx/${jobId}`);
        setAiProgress(data);
        if (data.status === "done") { clearInterval(interval); resolve(data); }
        else if (data.status === "error") { clearInterval(interval); reject(new Error(data.error || "Échec de la génération.")); }
      } catch (e) { clearInterval(interval); reject(e); }
    }, 2000);
  });

  const generateFromDocx = async () => {
    if (!aiFile) return toast.error("Choisissez un fichier Word (.docx).");
    setAiLoading(true);
    setAiProgress(null);
    const fd = new FormData();
    fd.append("file", aiFile);
    fd.append("generate_quiz", aiQuiz);
    if (aiQuiz) fd.append("quiz_question_count", aiQuizCount || 8);
    if (aiFiliere) fd.append("filiere", aiFiliere);
    try {
      const { data } = await api.post("/admin/generate-course-from-docx", fd);
      setAiProgress({ status: "processing", sections_done: 0, sections_total: data.sections_total });
      const result = await pollJob(data.job_id);
      toast.success(`Cours généré (${result.sections_count} section(s), ${result.lessons_count} chapitre(s)) — à relire avant publication.`);
      setAiOpen(false); setAiFile(null); setAiFiliere(""); setAiProgress(null);
      navigate(`/app/cours/${result.course_id}`);
    } catch (e) { toast.error(e.response ? apiError(e) : e.message); }
    finally { setAiLoading(false); }
  };

  const filieres = academic.filter((a) => a.type === "filiere");
  const promotionsForFiliere = academic.filter((a) => a.type === "promotion" && a.parent === pickFiliereId);
  const filiereName = filieres.find((f) => f.id === pickFiliereId)?.name || "";

  const create = async () => {
    try {
      const { data } = await api.post("/courses", { ...form, filiere: filiereName });
      if (coverFile) {
        const fd = new FormData();
        fd.append("file", coverFile);
        await api.post(`/courses/${data.id}/cover`, fd);
      }
      if (pickPromotionId) {
        await api.post(`/courses/${data.id}/promotions`, { promotion_ids: [pickPromotionId] });
      }
      toast.success("Cours créé");
      setOpen(false);
      setForm(EMPTY);
      setCoverFile(null);
      setPickFiliereId(""); setPickPromotionId("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Formations</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            {user.role === "etudiant" ? "Mes cours" : "Cours"}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {user.role === "admin" && (
            <Dialog open={aiOpen} onOpenChange={(o) => { setAiOpen(o); if (!o) { setAiFile(null); setAiFiliere(""); setAiProgress(null); setAiQuizCount(8); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="generate-course-ai-btn">
                  <Sparkle size={18} className="mr-2" /> Générer depuis un Word (IA)
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Générer un cours depuis un document Word</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Document Word (.docx)</Label>
                    <Input type="file" accept=".docx" onChange={(e) => setAiFile(e.target.files[0] || null)} disabled={aiLoading} data-testid="ai-docx-file" />
                    {aiFile && <p className="mt-1 text-xs text-muted-foreground">{aiFile.name}</p>}
                  </div>
                  <div><Label>Filière (optionnel)</Label><Input value={aiFiliere} onChange={(e) => setAiFiliere(e.target.value)} disabled={aiLoading} /></div>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-3 text-sm">
                    <Checkbox checked={aiQuiz} onCheckedChange={(v) => setAiQuiz(!!v)} disabled={aiLoading} />
                    <span>
                      <span className="font-medium">Générer aussi un quiz</span>
                      <span className="block text-xs text-muted-foreground">Questions à choix multiples à partir du contenu du document.</span>
                    </span>
                  </label>
                  {aiQuiz && (
                    <div className="pl-1">
                      <Label>Nombre de questions</Label>
                      <Input type="number" min={1} max={30} value={aiQuizCount} onChange={(e) => setAiQuizCount(e.target.value)} disabled={aiLoading} className="w-28" data-testid="ai-quiz-count" />
                      <p className="mt-1 text-xs text-muted-foreground">Entre 1 et 30.</p>
                    </div>
                  )}
                  <p className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
                    Le cours est créé <strong>non publié</strong> — relisez et ajustez le contenu généré avant de le rendre accessible aux étudiants. Le traitement se fait en arrière-plan et peut prendre plusieurs minutes pour un document volumineux — vous pouvez patienter sans risque de coupure.
                  </p>
                  {aiProgress && (
                    <div className="rounded-lg border border-border p-3" data-testid="ai-progress">
                      <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Génération des sections...</span>
                        <span className="font-medium">{aiProgress.sections_done ?? 0} / {aiProgress.sections_total ?? "?"}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${aiProgress.sections_total ? ((aiProgress.sections_done ?? 0) / aiProgress.sections_total) * 100 : 0}%` }} />
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={generateFromDocx} disabled={aiLoading} className="bg-primary hover:bg-primary/90" data-testid="confirm-generate-ai-btn">
                    <FileDoc size={16} className="mr-2" /> {aiLoading ? "Génération en cours..." : "Générer"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canCreate && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setCoverFile(null); setPickFiliereId(""); setPickPromotionId(""); } }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-course-btn">
                <Plus size={18} className="mr-2" /> Nouveau cours
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Créer un cours</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title" /></div>
                <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MGT-101" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-desc" /></div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Filière</Label>
                    <Select value={pickFiliereId} onValueChange={(v) => { setPickFiliereId(v); setPickPromotionId(""); }}>
                      <SelectTrigger data-testid="course-filiere"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        {filieres.length === 0 && <SelectItem value="__none" disabled>Aucune filière — voir Gestion académique</SelectItem>}
                        {filieres.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Promotion (optionnel)</Label>
                    <Select value={pickPromotionId} onValueChange={setPickPromotionId} disabled={!pickFiliereId}>
                      <SelectTrigger data-testid="course-promotion"><SelectValue placeholder={pickFiliereId ? "Choisir..." : "Filière d'abord"} /></SelectTrigger>
                      <SelectContent>
                        {promotionsForFiliere.length === 0 && <SelectItem value="__none" disabled>Aucune promotion</SelectItem>}
                        {promotionsForFiliere.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Choisir une promotion donne immédiatement accès au cours à tous ses étudiants (modifiable ensuite dans "Accès & salle virtuelle").</p>
                <div><Label>Lien visioconférence (optionnel)</Label><Input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://meet.jit.si/..." data-testid="course-meeting" /></div>
                <div>
                  <Label>Image de couverture</Label>
                  <div className="mt-1 flex gap-2">
                    <button type="button" onClick={() => { setForm({ ...form, cover_image: "" }); setCoverFile(null); }} className={`h-14 w-20 overflow-hidden rounded border-2 ${!coverFile && !form.cover_image ? "border-primary" : "border-transparent"}`} title="Monogramme généré automatiquement" data-testid="cover-monogram-option">
                      <CourseMonogram code={form.code} title={form.title} className="h-full w-full" />
                    </button>
                    {COVERS.map((c) => (
                      <button key={c} type="button" onClick={() => { setForm({ ...form, cover_image: c }); setCoverFile(null); }} className={`h-14 w-20 overflow-hidden rounded border-2 ${!coverFile && form.cover_image === c ? "border-primary" : "border-transparent"}`}>
                        <img src={c} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Sans image choisie, un monogramme généré à partir du code du cours est utilisé automatiquement.</p>
                  <div className="mt-2">
                    <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/*" onChange={(e) => setCoverFile(e.target.files[0] || null)} data-testid="course-cover-file" />
                    {coverFile && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                        <img src={URL.createObjectURL(coverFile)} alt="Aperçu" className="h-14 w-20 rounded border-2 border-primary object-cover" />
                        Image personnalisée sélectionnée ({coverFile.name})
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="save-course-btn">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {courses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          {user.role === "etudiant"
            ? "Aucun cours ne vous est attribué. Un professeur doit donner accès à votre groupe."
            : "Aucun cours pour le moment."}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => navigate(`/app/cours/${c.id}`)}
              className="group cursor-pointer overflow-hidden rounded-xl border border-border bg-card transition-transform hover:-translate-y-1 hover:shadow-lg"
              data-testid={`course-card-${c.id}`}
            >
              <div className="relative h-36 overflow-hidden">
                {coverSrc(c.cover_image) ? (
                  <img src={coverSrc(c.cover_image)} alt={c.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                ) : (
                  <CourseMonogram code={c.code} title={c.title} className="h-full w-full transition-transform group-hover:scale-105" />
                )}
                <div className="absolute right-2 top-2 flex gap-1">
                  {c.meeting_url && <Badge className="bg-accent hover:bg-accent"><VideoCamera size={12} className="mr-1" weight="fill" /> Live</Badge>}
                  {c.published ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Publié</Badge>
                  ) : (
                    <Badge variant="secondary">Brouillon</Badge>
                  )}
                </div>
              </div>
              <div className="p-5">
                {c.code && <p className="text-xs font-semibold uppercase tracking-wider text-accent">{c.code}</p>}
                <h3 className="mt-1 font-display text-lg font-bold leading-tight">{c.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.description}</p>
                {user.role !== "etudiant" && (c.filiere_labels?.length > 0 || c.promotion_labels?.length > 0 || c.semestre_labels?.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.filiere_labels?.map((label) => (
                      <Badge key={`f-${label}`} variant="secondary" className="text-[10px]">{label} (entière)</Badge>
                    ))}
                    {c.promotion_labels?.map((label) => (
                      <Badge key={`p-${label}`} variant="secondary" className="text-[10px]">{label}</Badge>
                    ))}
                    {c.semestre_labels?.map((label) => (
                      <Badge key={`s-${label}`} variant="secondary" className="text-[10px]">{label}</Badge>
                    ))}
                  </div>
                )}
                {user.role === "etudiant" && (
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Progression</span>
                      <span className="font-semibold text-primary">{c.progress}%</span>
                    </div>
                    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                      <div className="absolute inset-0 flex" style={{ clipPath: `inset(0 ${100 - c.progress}% 0 0)` }}>
                        <span className="h-full flex-1" style={{ background: "hsl(var(--campus-orange))" }} />
                        <span className="h-full flex-1 bg-white" />
                        <span className="h-full flex-1" style={{ background: "hsl(var(--campus-green))" }} />
                      </div>
                    </div>
                    {c.next_deadline && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock size={13} className="shrink-0 text-accent" />
                        <span className="truncate">
                          {c.next_deadline.type === "quiz" ? "Quiz" : "Devoir"} <strong className="text-foreground">{c.next_deadline.title}</strong> · {new Date(c.next_deadline.due_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        </span>
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <UsersIcon size={14} /> {c.students_count} étudiants
                  </span>
                  <span className="text-xs text-muted-foreground">{c.teacher_name}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
